/**
 * DimensionNav Component
 *
 * Left sidebar navigation for the analysis report, structured into three semantic groups:
 * - **Analysis**: Synthesized cross-cutting views (Executive Summary, Risk Assessment,
 *   Defensibility, AI Maturity)
 * - **Dimensions**: The 5 assessment domains (Architecture, SDLC, DevOps & Infrastructure,
 *   Product Management, Team & Organization)
 * - **Scans**: Raw point-in-time scan evidence (Source Code, Infrastructure)
 *
 * Executive Summary serves as the landing page, so it is highlighted when the pathname
 * matches either the bare basePath or the /executive-summary sub-path.
 *
 * @param basePath - The root path for this assessment's analysis section
 *   (e.g. `/assessments/abc123/analysis`)
 */

import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  FileText,
  Building2,
  GitBranch,
  Server,
  Target,
  Users,
  Grid3X3,
  Shield,
  Brain,
  Code,
} from "lucide-react";
import { ReportSidebarActions } from "@/components/layout/ReportSidebarActions";

interface DimensionNavProps {
  basePath: string;
}

interface NavItem {
  name: string;
  slug: string;
  icon: React.ComponentType<{ className?: string }>;
  isNew?: boolean;
  subtitle?: string;
}

const analysisItems: NavItem[] = [
  { name: "Executive Summary", slug: "executive-summary", icon: FileText },
  { name: "Risk Assessment", slug: "risk-matrix", icon: Grid3X3 },
  { name: "Defensibility", slug: "defensibility", icon: Shield, isNew: true },
  { name: "AI Maturity", slug: "ai-maturity", icon: Brain, isNew: true },
];

const dimensionItems: NavItem[] = [
  { name: "Architecture", slug: "architecture", icon: Building2 },
  { name: "SDLC", slug: "sdlc", icon: GitBranch },
  {
    name: "DevOps & Infrastructure",
    slug: "devops-infrastructure",
    icon: Server,
  },
  { name: "Product Management", slug: "product-management", icon: Target },
  { name: "Team & Organization", slug: "team-organization", icon: Users },
];

const scansItems: NavItem[] = [
  { name: "Source Code", slug: "code-scan", icon: Code },
  { name: "Infrastructure", slug: "infra-scan", icon: Server },
];

interface NavLinkProps {
  item: NavItem;
  to: string;
  isActive: boolean;
}

/**
 * Renders a single nav link item with optional "New" badge and subtitle.
 *
 * @param item - The nav item configuration
 * @param to - The full href for the link
 * @param isActive - Whether this item is currently active
 */
function NavLink({ item, to, isActive }: NavLinkProps) {
  const Icon = item.icon;

  return (
    <Link
      to={to}
      data-testid={`dimension-nav-link-${item.slug.replaceAll("/", "-")}`}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
        isActive
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-accent",
      )}
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span className="flex-1">{item.name}</span>
      {item.subtitle && (
        <span
          className={`text-xs font-normal ${isActive ? "text-primary-foreground/70" : "text-muted-foreground"}`}
        >
          {item.subtitle}
        </span>
      )}
      {item.isNew && (
        <span
          className={`text-xs font-semibold px-1.5 py-0.5 rounded ${isActive ? "bg-primary-foreground/20 text-primary-foreground" : "bg-primary/10 text-primary"}`}
        >
          New
        </span>
      )}
    </Link>
  );
}

/**
 * DimensionNav renders the left sidebar navigation for analysis report pages.
 *
 * It organises navigation into three labeled groups ("Analysis", "Dimensions",
 * and "Scans") and tracks the active item via the current pathname from
 * `useLocation`.
 *
 * @param basePath - Root path for the assessment's analysis section,
 *   e.g. `/assessments/abc123/analysis`
 * @returns The sidebar nav element
 * @throws {Error} If rendered outside a Router context (useLocation dependency)
 *
 * @example
 * ```tsx
 * <DimensionNav basePath="/assessments/abc123/analysis" />
 * ```
 */
export function DimensionNav({ basePath }: DimensionNavProps): JSX.Element {
  const { pathname } = useLocation();

  // Extract the active slug by removing the basePath prefix
  const suffix = pathname.startsWith(basePath + "/")
    ? pathname.slice(basePath.length + 1)
    : "";

  /**
   * Determines whether a given nav item should be marked active.
   *
   * Executive Summary is treated as the landing page, so it is active when
   * the pathname is exactly the basePath (no sub-path) as well as when it
   * matches its own slug.
   *
   * Slugs with prefix-matching support nested routes: e.g. slug `infra-scan`
   * stays active while on the child route `/infra-scan/findings`.
   *
   * @param slug - The item's URL slug (e.g. `infra-scan`, `code-scan`)
   * @returns `true` if the item should be highlighted
   */
  function isItemActive(slug: string): boolean {
    if (slug === "executive-summary") {
      return suffix === "" || suffix === "executive-summary";
    }
    return suffix === slug || suffix.startsWith(slug + "/");
  }

  const groups: Array<{ label: string; testId: string; items: NavItem[] }> = [
    {
      label: "Analysis",
      testId: "dimension-nav-group-analysis",
      items: analysisItems,
    },
    {
      label: "Dimensions",
      testId: "dimension-nav-group-dimensions",
      items: dimensionItems,
    },
    {
      label: "Scans",
      testId: "dimension-nav-group-scans",
      items: scansItems,
    },
  ];

  return (
    // STA-319: `h-full overflow-y-auto` ensures the sidebar scrolls
    // independently inside the bounded flex parent (AssessmentShell wraps the
    // sidebar+content row in `flex min-h-0 flex-1 overflow-hidden`). Without
    // overflow-y-auto, taller-than-viewport nav content was clipped and
    // unreachable on shorter viewports.
    <nav className="w-64 flex-shrink-0 h-full overflow-y-auto border-r border-border bg-card">
      <div className="py-6 px-4">
        {groups.flatMap((group, index) => {
          const groupNode = (
            <div key={`group-${group.testId}`}>
              <p
                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-3 mb-2"
                data-testid={group.testId}
              >
                {group.label}
              </p>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavLink
                    key={item.slug}
                    item={item}
                    to={`${basePath}/${item.slug}`}
                    isActive={isItemActive(item.slug)}
                  />
                ))}
              </div>
            </div>
          );
          if (index === groups.length - 1) return [groupNode];
          return [
            groupNode,
            <div
              key={`divider-${group.testId}`}
              className="h-px bg-border my-3"
            />,
          ];
        })}

        {/* Report actions: PDF download, investor sharing (STA-228) */}
        <ReportSidebarActions />
      </div>
    </nav>
  );
}
